import {
  DialogModal,
  Grid,
  Loader,
} from "@pushpress/shared-components";
import { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { PlanType } from "../../../apis/subscriptionApi";
import {
  convertToCreatePlanBody,
  convertToPlanForm,
  convertToUpdatePlanBody,
  createPlanFormDefaultValues,
  defaultBillingOption,
  getPlanDrawerTitle,
} from "./PlanDrawer.utils";
import {
  usePlanQuery,
  usePostPlanMutation,
  usePutPlanMutation,
} from "../../../apis/subscriptionApi.hooks";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { PlanForm } from "./PlanDrawer.types";
import UpdateSubscriptionsModal from "./UpdateSubscriptionsModal/UpdateSubscriptionsModal";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { PlanTemplate } from "../PlanTemplates/plan-templates";
import { SectionProvider } from "./hooks/useSectionContext";
import PlanModalSidebar from "./components/sidebar/PlanModalSidebar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@pushpress/pushpress-ui";
import PlanModalSections from "./components/PlanModalSections";

export interface PlanV2ModalProps {
  visible: boolean;
  duplicate?: boolean;
  planUuid?: string;
  planType?: PlanType;
  planTemplate?: PlanTemplate;
  onClose: VoidFunction;
  onSuccess?: VoidFunction;
}

function PlanV2Modal(props: PlanV2ModalProps) {
  const { visible } = props;
  
  if (!visible) return null;

  return <PlanV2ModalContent {...props} />;
}

function PlanV2ModalContent(props: PlanV2ModalProps) {
  const {
    planUuid,
    planType: planTypeProp,
    planTemplate,
    duplicate,
    onClose,
    onSuccess,
    visible,
  } = props;

  const { trackEvent } = useAnalytics();

  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);
  const [updateSubscriptionsVisible, setUpdateSubscriptionsVisible] = useState(false);
  const [planType, setPlanType] = useState<PlanType>(planTypeProp ?? "recurring");

  const { data: plan } = usePlanQuery(
    planUuid!,
    { include: ["activeSubscriptionsCount"] },
    {
      enabled: !!planUuid,
    },
  );

  const createPlanMutation = usePostPlanMutation();
  const updatePlanMutation = usePutPlanMutation();

  const methods = useForm<PlanForm>({
    mode: "onTouched",
    defaultValues: {
      ...createPlanFormDefaultValues(planType, planTemplate),
      prices: [defaultBillingOption],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    getValues,
  } = methods;

  const [
    price,
    sessionsCount,
    sessionsPeriod,
    attendanceLimit,
    priorityHoursInAdvance,
    perPersonLimitNum,
  ] = useWatch({
    control,
    name: [
      "price",
      "sessionsCount",
      "sessionsPeriod",
      "attendanceLimit",
      "priorityHoursInAdvance",
      "perPersonLimitNum",
    ],
  });

  // Function to convert field names to human-readable labels
  const getFieldLabel = (fieldName: string): string => {
    const fieldLabels: Record<string, string> = {
      name: "Plan name",
      clientStatus: "Client status",
      planCategory: "Category",
      prices: "Pricing options",
      "prices.0.price": "Price",
      "prices.0.billEveryCount": "Bill every count",
      "prices.0.billEveryInterval": "Bill every interval",
      classes: "Classes",
      sessionsCount: "Sessions count",
      sessionsPeriod: "Sessions period",
      sessionsExpireCount: "Sessions expire count",
      sessionsExpireInterval: "Sessions expire interval",
      quantityAvailable: "Quantity available",
      perPersonLimitNum: "Per person limit",
      priorityHoursInAdvance: "Priority hours in advance",
      "appointmentCredits.0.appointmentType": "Appointment type",
      "appointmentCredits.0.quantity": "Appointment quantity",
      "appointmentCredits.0.expirationDays": "Appointment expiration days",
      "appointmentCredits.0.value": "Appointment value",
    };

    // Handle nested field names like prices.0.price
    if (fieldName.includes('.')) {
      const parts = fieldName.split('.');
      if (parts[0] === 'prices' && parts[2]) {
        const fieldKey = `prices.0.${parts[2]}`;
        return fieldLabels[fieldKey] || parts[2];
      }
      if (parts[0] === 'appointmentCredits' && parts[2]) {
        const fieldKey = `appointmentCredits.0.${parts[2]}`;
        return fieldLabels[fieldKey] || parts[2];
      }
    }

    return fieldLabels[fieldName] || fieldName;
  };

  // Validation function to check required fields
  const validateRequiredFields = (values: PlanForm): { isValid: boolean; firstInvalidField?: string } => {
    // Basic required fields
    if (!values.name?.trim()) {
      return { isValid: false, firstInvalidField: "name" };
    }
    
    if (!values.clientStatus?.value) {
      return { isValid: false, firstInvalidField: "clientStatus" };
    }
    
    if (!values.planCategory?.uuid) {
      return { isValid: false, firstInvalidField: "planCategory" };
    }

    // Check if there are any pricing options
    const hasPricingOptions = values.prices && values.prices.length > 0;
    if (!hasPricingOptions) {
      return { isValid: false, firstInvalidField: "prices" };
    }

    // Validate each pricing option
    for (let i = 0; i < (values.prices?.length || 0); i++) {
      const price = values.prices![i];
      if (!price.price || price.price <= 0) {
        return { isValid: false, firstInvalidField: `prices.${i}.price` };
      }
      if (!price.billEveryCount || price.billEveryCount <= 0) {
        return { isValid: false, firstInvalidField: `prices.${i}.billEveryCount` };
      }
      if (!price.billEveryInterval?.value) {
        return { isValid: false, firstInvalidField: `prices.${i}.billEveryInterval` };
      }
    }

    // Class access validation
    if (values.classAccess?.value === "some") {
      if (!values.classes || values.classes.length === 0) {
        return { isValid: false, firstInvalidField: "classes" };
      }
    }

    // Sessions validation based on plan type and settings
    if (planType === "session-pack") {
      if (!values.sessionsCount || values.sessionsCount <= 0) {
        return { isValid: false, firstInvalidField: "sessionsCount" };
      }
      if (values.sessionsExpire && (!values.sessionsExpireCount || values.sessionsExpireCount <= 0)) {
        return { isValid: false, firstInvalidField: "sessionsExpireCount" };
      }
      if (values.sessionsExpire && !values.sessionsExpireInterval?.value) {
        return { isValid: false, firstInvalidField: "sessionsExpireInterval" };
      }
    } else if (planType === "recurring" || planType === "non-recurring") {
      if (values.attendanceLimit?.value === "limited") {
        if (!values.sessionsCount || values.sessionsCount <= 0) {
          return { isValid: false, firstInvalidField: "sessionsCount" };
        }
        if (!values.sessionsPeriod?.value) {
          return { isValid: false, firstInvalidField: "sessionsPeriod" };
        }
      }
    }

    // Advanced options validation
    if (values.hasLimit && (!values.quantityAvailable || values.quantityAvailable <= 0)) {
      return { isValid: false, firstInvalidField: "quantityAvailable" };
    }

    if (values.perPersonLimit && (!values.perPersonLimitNum || values.perPersonLimitNum <= 0)) {
      return { isValid: false, firstInvalidField: "perPersonLimitNum" };
    }

    if (values.priorityBooking && (values.priorityHoursInAdvance === undefined || values.priorityHoursInAdvance === null || values.priorityHoursInAdvance < 0)) {
      return { isValid: false, firstInvalidField: "priorityHoursInAdvance" };
    }

    // Appointment credits validation
    if (values.appointmentCredits && values.appointmentCredits.length > 0) {
      for (let i = 0; i < values.appointmentCredits.length; i++) {
        const credit = values.appointmentCredits[i];
        if (!credit.appointmentType?.uuid) {
          return { isValid: false, firstInvalidField: `appointmentCredits.${i}.appointmentType` };
        }
        if (!credit.quantity || credit.quantity <= 0) {
          return { isValid: false, firstInvalidField: `appointmentCredits.${i}.quantity` };
        }
        if (!credit.expirationDays || credit.expirationDays <= 0) {
          return { isValid: false, firstInvalidField: `appointmentCredits.${i}.expirationDays` };
        }
        if (credit.value === undefined || credit.value === null || credit.value < 0) {
          return { isValid: false, firstInvalidField: `appointmentCredits.${i}.value` };
        }
      }
    }

    return { isValid: true };
  };

  // Function to focus on the first invalid field
  const focusOnInvalidField = (fieldName: string) => {
    setTimeout(() => {
      // Try to find the element by name attribute first
      let element = document.querySelector(`[name="${fieldName}"]`) as HTMLElement;
      
      // If not found by name, try to find by the field path
      if (!element) {
        const fieldPath = fieldName.split('.');
        if (fieldPath.length > 0) {
          // For nested fields like prices.0.price, try different selectors
          const lastPart = fieldPath[fieldPath.length - 1];
          element = document.querySelector(`[name*="${lastPart}"]`) as HTMLElement;
        }
      }
      
      // If still not found, try to find by id
      if (!element) {
        element = document.getElementById(fieldName) as HTMLElement;
      }
      
      // If still not found, try to find by label association
      if (!element) {
        const label = document.querySelector(`label[for="${fieldName}"]`);
        if (label) {
          const id = label.getAttribute('for');
          if (id) {
            element = document.getElementById(id) as HTMLElement;
          }
        }
      }
      
      // Try to find by data attribute for React Hook Form fields
      if (!element) {
        element = document.querySelector(`[data-testid="${fieldName}"]`) as HTMLElement;
      }
      
      // Try to find by aria-describedby
      if (!element) {
        element = document.querySelector(`[aria-describedby*="${fieldName}"]`) as HTMLElement;
      }
      
      // Try to find by input type and placeholder
      if (!element) {
        element = document.querySelector(`input[placeholder*="${fieldName}"]`) as HTMLElement;
      }
      
      // Try to find by label text
      if (!element) {
        const labels = document.querySelectorAll('label');
        for (const label of labels) {
          if (label.textContent?.toLowerCase().includes(fieldName.toLowerCase())) {
            const forAttr = label.getAttribute('for');
            if (forAttr) {
              element = document.getElementById(forAttr) as HTMLElement;
              break;
            }
          }
        }
      }
      
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Add a visual highlight to the field
        element.style.outline = '2px solid #ef4444';
        element.style.outlineOffset = '2px';
        
        // Remove the highlight after 3 seconds
        setTimeout(() => {
          element.style.outline = '';
          element.style.outlineOffset = '';
        }, 3000);
      } else {
        // If we can't find the specific element, scroll to the top of the form
        const formContainer = document.querySelector('.tw-bg-white') as HTMLElement;
        if (formContainer) {
          formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 100);
  };

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseVisible(true);
    } else {
      trackEvent("Plan Modal Closed", {
        plan_uuid: planUuid,
        plan_type: planType,
        is_duplicate: !!duplicate,
        from_template: !!planTemplate,
        template_id: planTemplate?.id,
        with_changes: false,
      });
      onClose();
    }
  };

  const savePlan = async (values: PlanForm) => {
    if (!planType) {
      throw new Error("Plan type is required");
    }
    try {
      const body = convertToCreatePlanBody(planType, values);
      const response = await createPlanMutation.mutateAsync({ body });

      let originalPrice = null;
      let priceModified = false;

      if (duplicate) {
        originalPrice = originalPlanPrice;
        priceModified = values.price !== originalPlanPrice;
      } else if (planTemplate) {
        originalPrice = planTemplate.recommendedPrice;
        priceModified = values.price !== planTemplate.recommendedPrice;
      }

      trackEvent("Plan Create Success", {
        plan_uuid: response?.data?.planUuid,
        plan_type: planType,
        saved_price: values.price,
        original_price: originalPrice,
        price_modified: priceModified,
        is_duplicate: !!duplicate,
        from_template: !!planTemplate,
        template_id: planTemplate?.id,
        source: duplicate ? "duplicate" : planTemplate ? "template" : "scratch",
      });

      onSuccess?.();
      onClose?.();
      toast.success("Plan created successfully", defaultToastOptions);
    } catch (error) {
      trackEvent("Plan Create Failure", {
        plan_type: planType,
        is_duplicate: !!duplicate,
        from_template: !!planTemplate,
        template_id: planTemplate?.id,
      });
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const updatePlan = async (
    values: PlanForm,
    update?: {
      price: boolean;
      sessions: boolean;
    },
  ) => {
    if (!planUuid) {
      throw new Error("Plan uuid is required");
    }
    if (!planType) {
      throw new Error("Plan type is required");
    }
    try {
      const body = convertToUpdatePlanBody(planUuid, planType, values, update);
      await updatePlanMutation.mutateAsync({ body });
      trackEvent("Plan Edit Success", {
        plan_uuid: planUuid,
        plan_type: planType,
        price: values.price,
        original_price: plan?.amount,
        price_modified: values.price !== plan?.amount,
        update_price: update?.price,
        update_sessions: update?.sessions,
        with_changes: isDirty,
      });
      onSuccess?.();
      onClose?.();
      toast.success("Plan updated successfully", defaultToastOptions);
    } catch (error) {
      trackEvent("Plan Edit Failure", {
        plan_uuid: planUuid,
        plan_type: planType,
      });
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const handleSave = () => {
    
    const values = getValues();
    const validation = validateRequiredFields(values);
    
    if (!validation.isValid) {
      const fieldName = validation.firstInvalidField || 'required field';
      toast.error(`Please fill in the required field: ${getFieldLabel(fieldName)}`, defaultToastOptions);
      
      if (validation.firstInvalidField) {
        focusOnInvalidField(validation.firstInvalidField);
      }
      return;
    }

    const save = async (values: PlanForm) => {
      if (planUuid && !duplicate) {
        await updatePlan(values);
      } else {
        await savePlan(values);
      }
    };
    
    // If valid, proceed with save
    handleSubmit(save)();
  };

  const handleConfirmUpdateSubscriptions = (update: {
    price: boolean;
    sessions: boolean;
  }) => {
    const values = getValues();
    updatePlan(values, update);
  };

  const [originalPlanPrice, setOriginalPlanPrice] = useState<number | null>(null);

  useEffect(() => {
    if (planUuid && plan) {
      setPlanType(plan.type);
      if (duplicate) {
        setOriginalPlanPrice(plan.amount);
      }
      reset(convertToPlanForm(plan, duplicate, true));
    }
  }, [reset, planUuid, plan, duplicate]);

  if (!planType || !methods) {
    return (
      <Grid container justifyContent="center" className="p-6">
        <Loader />
      </Grid>
    );
  }

  const saveLoading = createPlanMutation.isLoading || updatePlanMutation.isLoading;
  // if any of the prices are different, then the price is changed
  let hasPriceChanged = false;

  if(plan?.prices?.length !== getValues("prices").length) {
    hasPriceChanged = true;
  } else {
    for (const [index] of plan.prices.entries()) {
      if (plan.prices[index].amount !== getValues("prices")[index].price) {
        hasPriceChanged = true;
        break;  
      }
    }
  }
  
  const hasSessionsCountChanged =
    plan?.limitCheckinsQuantity !== sessionsCount ||
    plan?.limitCheckinsType !== sessionsPeriod?.value;
  const hasLimitCheckinsChanged =
    plan?.limitCheckins !== (attendanceLimit?.value === "limited");

  return (
    <>
      <Dialog open={visible} onOpenChange={handleClose}>
        <DialogContent
          className="!tw-p-0 !tw-w-full !tw-max-w-none"
          style={{ width: "100vw", maxWidth: "100vw", height: "100vh", padding: 0 }}
        >
          <DialogHeader>
            <DialogTitle>
              <div className="tw-flex tw-items-center tw-justify-center tw-text-xl tw-font-semibold tw-mt-4">
                <span className="tw-text-xl tw-font-semibold">{getPlanDrawerTitle(planType ?? 'recurring')}</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          <SectionProvider defaultSection="basic">
            <div className="tw-flex tw-flex-col" style={{ height: "calc(100vh - 4rem)" }}>
              <div className="tw-flex tw-flex-1 tw-overflow-hidden">
                <PlanModalSidebar />
                <div className="tw-flex-1 tw-overflow-auto tw-px-8">
                  {methods && (
                    <FormProvider {...methods}>
                      <PlanModalSections
                        control={control}
                        planType={planType}
                      />
                    </FormProvider>
                  )}
                </div>
              </div>
              <div className="tw-border-t tw-border-gray-200 tw-bg-white tw-px-8 tw-py-4">
                <button
                  onClick={handleSave}
                  disabled={saveLoading}
                  className="tw-w-full tw-bg-blue-600 tw-text-white tw-px-6 tw-py-3 tw-rounded-lg tw-font-semibold tw-shadow-sm tw-transition-colors hover:tw-bg-blue-700 disabled:tw-opacity-50"
                >
                  {saveLoading ? (
                    <span className="tw-flex tw-items-center tw-justify-center">
                      <Loader size={20} className="tw-mr-2" /> Saving...
                    </span>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </SectionProvider>
        </DialogContent>
      </Dialog>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={() => {
          trackEvent("Plan Modal Closed", {
            plan_uuid: planUuid,
            plan_type: planType,
            is_duplicate: !!duplicate,
            from_template: !!planTemplate,
            template_id: planTemplate?.id,
            with_changes: true,
            changes_discarded: true,
          });
          onClose();
        }}
        secondaryHandler={() => setConfirmCloseVisible(false)}
        closeModal={() => setConfirmCloseVisible(false)}
      />

      {planUuid && (
        <UpdateSubscriptionsModal
          hasLimitCheckinsChanged={hasLimitCheckinsChanged}
          hasPriceChanged={hasPriceChanged}
          hasSessionsCountChanged={hasSessionsCountChanged}
          newLimitCheckins={attendanceLimit?.value === "limited"}
          planUuid={planUuid}
          newPrice={price}
          newSessionsCount={sessionsCount}
          newSessionsPeriod={sessionsPeriod?.value}
          newPriorityBookingHours={priorityHoursInAdvance}
          newPerPersonLimitNum={perPersonLimitNum}
          visible={updateSubscriptionsVisible}
          activeSubscriptionsCount={plan?.activeSubscriptionsCount ?? 0}
          onClose={() => setUpdateSubscriptionsVisible(false)}
          onConfirm={handleConfirmUpdateSubscriptions}
        />
      )} 
    </>
  );
}

export default PlanV2Modal; 